import { Box, Grid, ResponsiveSection, Link, IconChevronRight16, Divider, Text } from '@stitch-fix/mode-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import { useInView } from 'react-intersection-observer';
import { formatMoney } from '@stitch-fix/format';
import { VisionStatusProvider } from '@stitch-fix/vision-shared';
import { useGetLookbookGalleryQuery } from './graphql/getLookbookGallery';
import CurrentGallery from './CurrentGallery';
import PreviousGallery from './PreviousGallery';
import GalleryItemLoadingPlaceholder from './CurrentGallery/GalleryItemLoadingPlaceholder';
import GalleryOptInOutToggle from './GalleryOptInOutToggle';
import { UpdateVisionOptInOutAnswerMutation, useUpdateVisionOptInOutAnswerMutation } from './graphql/updateVisionOptInOutAnswer';
import { useVisionGalleryViewedAtUpdateMutation } from './graphql/visionGalleryViewedAtUpdate';
import { ToastProvider, useToast } from '../../../utils/contexts/toast-provider';
import { AndroidNoGallery } from './NoGallery/AndroidNoGallery';
import { DesktopNoGallery } from './NoGallery/DesktopNoGallery';
import { isAndroidApp } from '../../../utils/android';
import VisionNotificationBanner from './VisionNotificationBanner';
import MissingFullBodyCta from './MissingFullBodyCta';
import { useHighValueReferralsAllocation } from './CurrentGallery/hooks/useHighValueReferralsAllocation';
import { useGalleryRestoration } from './hooks/useGalleryRestoration';
import { LookNavigationRestorationProvider } from './LookNavigationRestorationContext';
const DEFAULT_RECIPIENT_CREDIT_AMOUNT = '$25';
const formatRecipientCreditAmount = (recipientCredit?: {
  value: number;
  currency: string;
} | null) => {
  if (!recipientCredit?.currency) return DEFAULT_RECIPIENT_CREDIT_AMOUNT;
  const formattedCreditAmount = formatMoney(recipientCredit.value, {
    inCents: true
  });
  return recipientCredit.value % 100 === 0 ? formattedCreditAmount.replace(/\.00$/, '') : formattedCreditAmount;
};
const GalleryLoadingState = ({
  overlay = false
}: {
  overlay?: boolean;
}) => <div role="status" aria-label="Loading gallery" style={overlay ? {
  position: 'fixed',
  inset: 0,
  zIndex: 1,
  overflow: 'hidden',
  backgroundColor: 'white'
} : undefined}>
    <ResponsiveSection gutter={{
    sm: 'none'
  }} bgColor="gray-99">
      <Grid columns={{
      sm: 1,
      md: 2,
      lg: 3
    }} mt={10} spacing={{
      sm: 'none'
    }}>
        <GalleryItemLoadingPlaceholder />
        <GalleryItemLoadingPlaceholder />
        <GalleryItemLoadingPlaceholder />
      </Grid>
    </ResponsiveSection>
  </div>;
const LookbookGalleryContent = () => {
  const {
    addToast
  } = useToast();
  const {
    featureFlags
  } = useFeatureFlags('client', ['client.global.vision_notification', 'client.vision.share_url_enabled']);
  const {
    isPromoTreatment
  } = useHighValueReferralsAllocation();
  const {
    pageCount,
    lookId: restorationLookId,
    lookViewportTop: restorationLookViewportTop,
    scrollY: restorationScrollY,
    isInitialized: isRestorationInitialized,
    recordPageCount,
    recordPageRequest,
    recordLookNavigation,
    clearRestoredPosition
  } = useGalleryRestoration();
  const [isPreviousGalleryRestored, setIsPreviousGalleryRestored] = useState(false);
  const handlePreviousGalleryRestorationComplete = useCallback(() => setIsPreviousGalleryRestored(true), []);
  const hasRestoredPositionRef = useRef(false);
  const [hasRestoredPosition, setHasRestoredPosition] = useState(false);
  const showVisionNotification = featureFlags?.['client.global.vision_notification'];
  const isShareUrlEnabled = !!featureFlags?.['client.vision.share_url_enabled'];
  const {
    data,
    loading,
    error
  } = useGetLookbookGalleryQuery();
  const [previousGalleryImageCount, setPreviousGalleryImageCount] = useState<number | null>(null);
  const vision = data?.client?.vision;
  const rawBusinessLine = data?.client?.businessLine;
  const businessLine = rawBusinessLine === 'MENS' || rawBusinessLine === 'WOMENS' ? rawBusinessLine : 'WOMENS';
  const visionId = vision?.id ?? '';
  const currentLookBookId = vision?.currentLookBook?.id;
  const banner = vision?.notification?.banner ?? null;
  const isToggleAtTop = useRef<boolean | null>(null);
  const bannerIdRef = useRef<string | null>(null);
  const hasShownErrorToastRef = useRef(false);
  const lastRecordedLookbookIdRef = useRef<string | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [galleryInViewRef, galleryInView] = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const [visionGalleryViewedAtUpdate] = useVisionGalleryViewedAtUpdateMutation();
  const recordVisionGalleryView = useCallback(() => {
    if (!currentLookBookId || lastRecordedLookbookIdRef.current === currentLookBookId) {
      return;
    }
    lastRecordedLookbookIdRef.current = currentLookBookId;
    visionGalleryViewedAtUpdate({
      variables: {
        input: {
          id: currentLookBookId
        }
      }
    }).then(result => {
      if (result.data?.visionGalleryViewedAtUpdate?.result?.__typename === 'VisionGalleryViewedAtUpdateError') {
        lastRecordedLookbookIdRef.current = null;
      }
    }).catch(() => {
      lastRecordedLookbookIdRef.current = null;
    });
  }, [currentLookBookId, visionGalleryViewedAtUpdate]);
  useEffect(() => {
    // Trigger when the client visits /gallery and we have a lookbook id.
    recordVisionGalleryView();
  }, [recordVisionGalleryView]);
  useEffect(() => {
    if (galleryInView) {
      // Also trigger when the gallery module scrolls into view.
      recordVisionGalleryView();
    }
  }, [galleryInView, recordVisionGalleryView]);
  useEffect(() => {
    const nextBannerId = banner?.id ?? null;
    if (!nextBannerId) {
      bannerIdRef.current = null;
      setShowBanner(false);
      return;
    }
    if (bannerIdRef.current !== nextBannerId) {
      bannerIdRef.current = nextBannerId;
      setShowBanner(true);
    }
  }, [banner?.id]);
  useEffect(() => {
    if (loading) {
      hasShownErrorToastRef.current = false;
      return;
    }
    if (vision && !error) {
      hasShownErrorToastRef.current = false;
      return;
    }
    if (!hasShownErrorToastRef.current && (error || !vision)) {
      hasShownErrorToastRef.current = true;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch lookbook gallery:', error.message);
      }
      addToast({
        delay: 6000,
        status: 'error',
        message: 'Failed to fetch lookbook gallery'
      });
    }
  }, [addToast, error, loading, vision]);
  useEffect(() => {
    if (!isRestorationInitialized || !isPreviousGalleryRestored || hasRestoredPositionRef.current || !restorationLookId && restorationScrollY === null) {
      return undefined;
    }
    hasRestoredPositionRef.current = true;
    let didRestore = false;
    let firstFrame = 0;
    let secondFrame = 0;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        didRestore = true;
        if (restorationLookId) {
          const restoredLook = document.getElementById(`gallery-look-${restorationLookId}`);
          const fallback = document.getElementById('vision-gallery-restoration-end');
          if (restoredLook && restorationLookViewportTop !== null) {
            window.scrollTo({
              top: window.scrollY + restoredLook.getBoundingClientRect().top - restorationLookViewportTop,
              behavior: 'auto'
            });
          } else if (restorationScrollY !== null) {
            window.scrollTo({
              top: restorationScrollY,
              behavior: 'auto'
            });
          } else {
            (restoredLook ?? fallback)?.scrollIntoView({
              behavior: 'auto',
              block: restoredLook ? 'nearest' : 'end'
            });
          }
        } else if (restorationScrollY !== null) {
          window.scrollTo({
            top: restorationScrollY,
            behavior: 'auto'
          });
        }
        clearRestoredPosition();
        setHasRestoredPosition(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      if (!didRestore) hasRestoredPositionRef.current = false;
    };
  }, [clearRestoredPosition, isPreviousGalleryRestored, isRestorationInitialized, restorationLookId, restorationLookViewportTop, restorationScrollY]);
  if (!loading && isToggleAtTop.current === null && vision) {
    isToggleAtTop.current = !vision.isOptedInToRecurringLooks;
  }
  const hasRestorationTarget = !!restorationLookId || restorationScrollY !== null;
  const isRestoringPosition = !isRestorationInitialized || hasRestorationTarget && !hasRestoredPosition;
  const [updateVisionOptInOut] = useUpdateVisionOptInOutAnswerMutation({
    onCompleted: (_data: UpdateVisionOptInOutAnswerMutation) => {
      const result = _data.visionOptInUpdate?.result;
      if (result?.__typename === 'VisionOptInUpdateError') {
        // eslint-disable-next-line no-console
        console.error('Vision opt-in/out update failed:', result.message);
        addToast({
          delay: 6000,
          status: 'error',
          message: result.message
        });
      }
    }
  });
  const updateWeeklyGalleryOptIn = (optIn: boolean) => {
    addToast({
      delay: 6000,
      status: 'success',
      message: optIn ? "You'll see new images in your gallery soon." : "You won't see new images in your gallery."
    });
    updateVisionOptInOut({
      variables: {
        input: {
          optIn
        }
      },
      optimisticResponse: {
        visionOptInUpdate: {
          __typename: 'VisionOptInUpdatePayload',
          result: {
            __typename: 'VisionOptInUpdateSuccess',
            vision: {
              __typename: 'Vision',
              id: visionId,
              isOptedInToRecurringLooks: optIn
            }
          }
        }
      }
    });
  };
  if (loading) {
    return <GalleryLoadingState />;
  }
  if (error || !vision) {
    return null;
  }
  const optInOutToggle = <GalleryOptInOutToggle isOptedIn={vision.isOptedInToRecurringLooks} onChange={updateWeeklyGalleryOptIn} />;
  const {
    currentLookBook,
    notification
  } = vision;
  const recipientCreditAmount = formatRecipientCreditAmount(data.client?.referrals.incentive?.recipientCredit);
  const noCurrentLookbook = !currentLookBook;
  const hasCurrentGalleryImages = (currentLookBook?.looks.length ?? 0) > 0;
  const hasPreviousGalleryImages = (previousGalleryImageCount ?? 0) > 0;
  const allImagesDeleted = currentLookBook && currentLookBook.looks.length === 0;
  const galleryQuickActionModule = hasCurrentGalleryImages || hasPreviousGalleryImages ? vision.galleryQuickActionModule : undefined;
  const isMissingFullBodyPhoto = !data.client?.photoFullBody;
  if (noCurrentLookbook) {
    return isAndroidApp() ? <AndroidNoGallery /> : <DesktopNoGallery />;
  }
  return <>
      {isRestoringPosition ? <GalleryLoadingState overlay /> : null}
      <div ref={galleryInViewRef} style={{
      visibility: isRestoringPosition ? 'hidden' : 'visible'
    }}>
        {showVisionNotification && banner && showBanner && notification ? <VisionNotificationBanner notification={notification} onDismiss={() => setShowBanner(false)} /> : null}
        <ResponsiveSection gutter={{
        sm: 'none'
      }} maxWidth="1084px" mt={1.5} mb={1.5} px={1}>
          {isToggleAtTop.current ? optInOutToggle : null}
          <LookNavigationRestorationProvider recordLookNavigation={recordLookNavigation}>
            <CurrentGallery lookbook={currentLookBook} showEmptyState={allImagesDeleted && previousGalleryImageCount === 0} isPromoTreatment={isPromoTreatment} businessLine={businessLine} isShareUrlEnabled={isShareUrlEnabled} recipientCreditAmount={recipientCreditAmount} galleryQuickActionModule={galleryQuickActionModule} headerContent={isMissingFullBodyPhoto ? <MissingFullBodyCta /> : undefined} />
          </LookNavigationRestorationProvider>
          {!isMissingFullBodyPhoto ? <Box mt={2}>
              <Text family="regular" height="standard" scale="3" color="gray-16" mr={0.5}>
                Change your look?{' '}
              </Text>
              <Link size="tertiary" href="/profile/photos" endIcon={<IconChevronRight16 />}>
                Update your photos
              </Link>
            </Box> : null}
          <Divider variant="item" mt={2} />
          <Box mt={2} id="vision-gallery-restoration-end">
            <LookNavigationRestorationProvider recordLookNavigation={recordLookNavigation}>
              <PreviousGallery isShareUrlEnabled={isShareUrlEnabled} recipientCreditAmount={recipientCreditAmount} onLooksCountChange={setPreviousGalleryImageCount} pageCount={pageCount} isRestorationInitialized={isRestorationInitialized} onPageRequest={recordPageRequest} onPageCountChange={recordPageCount} onRestorationComplete={handlePreviousGalleryRestorationComplete} />
            </LookNavigationRestorationProvider>
          </Box>
          {!isToggleAtTop.current ? optInOutToggle : null}
        </ResponsiveSection>
      </div>
    </>;
};
const LookbookGallery = () => {
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view',
    name: 'vision_gallery',
    source_app: 'categories-next-ui'
  });
  return <EventProvider>
      <VisionStatusProvider>
        <ToastProvider>
          <LookbookGalleryContent />
        </ToastProvider>
      </VisionStatusProvider>
    </EventProvider>;
};
export default LookbookGallery;